import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import MyAvatar from "src/components/MyAvatar"
import { selectIsAuthenticated, selectUsername } from "src/store/user"

import CreditsBadge from "src/components/CreditsBadge"
import { TeamIcon } from "src/components/icons"

import "./BaseLayout.css"

/* `layout` picks which shell the docks get: the default two fixed docks, "compact" (a rail plus
   one trimmed dock), or "phone" (no docks — the view puts its panels in sheets over the map). It
   is a class on <main> and nothing else; the widths live in App.css beside the originals, so the
   three shells cannot drift out of step with the breakpoints derived from them. */
const BaseLayout = ({ leftBar, rightBar, content, children, topBar, layout = "desktop" }) => {
  const { t } = useTranslation()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const username = useSelector(selectUsername)

  const openRegisterModal = () => {
    ModalService.show("RegisterModal", {
      onLogin: () => {
        ModalService.hideAll()
        ModalService.show("LoginModal", {
          onRegister: openRegisterModal,
        })
      },
    })
  }
  const openSettingsModal = () => ModalService.show("SettingsModal")
  // Who makes the game and how to help it. In the header on every page that has one, signed in or
  // not: somebody deciding whether to register is as welcome to write as a player with credits.
  const teamButton = (
    <button type="button" className="team-button" onClick={() => ModalService.show("TeamModal")} title={t("app.openTeam")} aria-label={t("app.openTeam")}>
      <TeamIcon size={18} />
    </button>
  )

  // The Game view passes a fixed top command bar (identity/round/resources/END TURN/settings —
  // see Game.js) that replaces this floating user chip entirely, rather than stacking both. Views
  // that don't pass one (Home/Lobby) keep the original floating header untouched.
  return (
    <>
      {topBar ? (
        topBar
      ) : (
        <header className="app-header">
          {/* Right-aligned, sized to content: a fixed flex split gave the chip one sixth of the
              window whatever it held, and credits + avatar + a long username ran off the edge. */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div>
              {isAuthenticated ? (
                <div className="user-info centered">
                  {/* The balance sits beside the name because a game that costs credits has to
                      show them: a player who cannot see what they have finds out about the entry
                      fee by being refused. */}
                  <CreditsBadge />
                  {teamButton}
                  {/* The avatar and the name are one control: a portrait beside a button reads
                      as part of it, and a player who pressed the picture got nothing. */}
                  <button type="button" className="user-icon-button" onClick={openSettingsModal} title={t("game.topBar.settings")}>
                    <MyAvatar alt={t("app.userIconAlt")} className="user-icon" />
                  </button>
                  <button onClick={openSettingsModal}>{username}</button>
                </div>
              ) : (
                <div className="open-login-register-modal centered">
                  {teamButton}
                  <button type="button" className="user-icon-button" onClick={openRegisterModal}>
                    <img src={require("src/assets/images/profile.png")} alt={t("app.userIconAlt")} className="user-icon" />
                  </button>
                  <button onClick={openRegisterModal}>{t("auth.headerRegister")}</button>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Views that only pass `children` (Home) get no docks at all — rendering `main` anyway
          would paint two empty fixed surface panels over their layout. */}
      {(content || leftBar || rightBar) && (
        <main className={`${topBar ? "with-topbar" : ""} ${layout === "desktop" ? "" : `layout-${layout}`}`.trim()}>
          <div className="leftBar">{leftBar}</div>
          <div className="content">{content}</div>
          <div className="rightBar">{rightBar}</div>
        </main>
      )}
      <div className="fullContent">{children}</div>
    </>
  )
}

export default BaseLayout
